// IntakeTopFilterPanel supplies the top filter controls for the intake filter dialog, including the main filter
// panel that has general student filters based on connections, cohorts, etc., and the summary of applied intake
// form filters.

import React from 'react';
import PropTypes from 'prop-types';

import IntakeMainFilterPanel from './IntakeMainFilterPanel';
import IntakeAppliedFilters from './IntakeAppliedFilters';

import { IntakeQuestion, ActiveIntakeFilter, SearchingUserPermissions } from './intakeTypes';
import { NotificationCenter } from 'utils/types';
import { studentFiltersAppliedEvent, intakeQuestionsLoadedEvent } from 'utils/events';
import { hot } from 'react-hot-loader';

/**
 * IntakeTopFilters presents the main list of student filters and the active list of intake question filters
 * in an accordion.
 *
 * @param {object} props permissions that control visible student filters, the list of intake questions,
 *                       the list of selected intake filters and callbacks for the filter summary to clear
 *                       questions.
 * @returns {React.Component} the intake filters
 */
export const IntakeTopFilters = (props) =>
  <div>
    <IntakeMainFilterPanel cohortsEnabled={props.searchingUserPermissions.cohortsEnabled}
                           notificationCenter={props.notificationCenter} />

    <IntakeAppliedFilters intakeQuestions={props.intakeQuestions}
                          activeIntakeFilters={props.activeIntakeFilters}
                          onClearFilter={props.onClearFilter}
                          onFilterHeightChanged={props.onFilterHeightChanged} />
  </div>;

IntakeTopFilters.defaultProps = {
  onFilterHeightChanged: () => null,
} ;

IntakeTopFilters.propTypes = {
  notificationCenter: NotificationCenter.isRequired,

  searchingUserPermissions: SearchingUserPermissions.isRequired,

  intakeQuestions: PropTypes.arrayOf(IntakeQuestion).isRequired,
  activeIntakeFilters: PropTypes.arrayOf(ActiveIntakeFilter).isRequired,

  onClearFilter: PropTypes.func,
  onFilterHeightChanged: PropTypes.func,
};

// IntakeTopFilterPanel owns the state for the intake filters and uses IntakeTopFilters to show
// the student and intake form filters.
class IntakeTopFilterPanel extends React.Component {
  static propTypes = {
    searchingUserPermissions: SearchingUserPermissions.isRequired,
    notificationCenter: NotificationCenter.isRequired,
    onFilterHeightChanged: PropTypes.func
  };

  static defaultProps = {
    onFilterHeightChanged: () => null,
  };

  constructor() {
    super();

    this.state = {
      intakeQuestions: [],
      activeIntakeFilters: []
    };
  }

  handleIntakeFiltersApplied = ({intakeElements}) => {
    if (!intakeElements || intakeElements === this.state.activeIntakeFilters) {
      return;
    }
    this.setState({activeIntakeFilters: intakeElements}, this.props.onFilterHeightChanged);
  };

  handleIntakeQuestionsLoaded = intakeQuestions => this.setState({intakeQuestions}, this.props.onFilterHeightChanged);

  handleClearFilter = (removedQuestionId) =>
    this.setState(prevState => ({
      activeIntakeFilters: prevState.activeIntakeFilters.filter(questionFilter =>
        questionFilter.questionId !== removedQuestionId
      )
    }), () => {
      this.notifyIntakeFilterCleared();
      this.props.onFilterHeightChanged();
    });

  notifyIntakeFilterCleared = () =>
    this.props.notificationCenter.fireEvent(studentFiltersAppliedEvent, {
      intakeElements: this.state.activeIntakeFilters
    });

  componentDidMount() {
    this.props.notificationCenter.addListener(intakeQuestionsLoadedEvent, this.handleIntakeQuestionsLoaded);
    this.props.notificationCenter.addListener(studentFiltersAppliedEvent, this.handleIntakeFiltersApplied);
  }

  componentWillUnmount() {
    this.props.notificationCenter.removeListener(intakeQuestionsLoadedEvent, this.handleIntakeQuestionsLoaded);
    this.props.notificationCenter.removeListener(studentFiltersAppliedEvent, this.handleIntakeFiltersApplied);
  }

  render() {
    return <IntakeTopFilters notificationCenter={this.props.notificationCenter}

                             searchingUserPermissions={this.props.searchingUserPermissions}
                             intakeQuestions={this.state.intakeQuestions}
                             activeIntakeFilters={this.state.activeIntakeFilters}

                             onClearFilter={this.handleClearFilter}
                             onFilterHeightChanged={this.props.onFilterHeightChanged} />;
  }
}

export default hot(module)(IntakeTopFilterPanel);
